import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  billingControllerCancelV1,
  billingControllerContactSalesV1,
  billingControllerInvoicesV1,
  billingControllerOverviewV1,
  billingControllerResumeV1,
  billingControllerRetryPaymentV1,
  billingControllerSubscribeV1,
  billingControllerUpdatePaymentMethodV1,
} from '@/api/generated/billing/billing';
import type {
  BillingControllerInvoicesV1Params,
  CardDto,
  CreateSalesRequestDto,
  SubscribeDto,
} from '@/api/generated/model';
import { currentUserKeys } from '@/lib/current-user';
import { settingsKeys } from '@/modules/settings';

export const billingKeys = {
  all: ['billing'] as const,
  overview: () => [...billingKeys.all, 'overview'] as const,
  invoices: {
    all: ['billing', 'invoices'] as const,
    list: (params: BillingControllerInvoicesV1Params) =>
      [...billingKeys.invoices.all, params] as const,
  },
};

/** The whole billing screen: plan, limits, usage, card on file, tier catalog. */
export function useBillingOverviewQuery() {
  return useQuery({
    queryKey: billingKeys.overview(),
    queryFn: () => billingControllerOverviewV1(),
  });
}

export function useInvoicesQuery(params: BillingControllerInvoicesV1Params) {
  return useQuery({
    queryKey: billingKeys.invoices.list(params),
    queryFn: () => billingControllerInvoicesV1(params),
    placeholderData: (previous) => previous,
  });
}

/**
 * Every subscription mutation changes three cached things at once: the billing
 * screen, the gate on `/users/me` (blocked ↔ active), and the agency's seat
 * limit. Invalidating all three here keeps the callers to one line.
 */
function useInvalidateBilling() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: billingKeys.all }),
      queryClient.invalidateQueries({ queryKey: currentUserKeys.all }),
      queryClient.invalidateQueries({ queryKey: settingsKeys.agency }),
    ]);
  };
}

export function useSubscribeMutation() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (dto: SubscribeDto) => billingControllerSubscribeV1(dto),
    onSuccess: invalidate,
  });
}

export function useCancelSubscriptionMutation() {
  const invalidate = useInvalidateBilling();
  return useMutation({ mutationFn: () => billingControllerCancelV1(), onSuccess: invalidate });
}

export function useResumeSubscriptionMutation() {
  const invalidate = useInvalidateBilling();
  return useMutation({ mutationFn: () => billingControllerResumeV1(), onSuccess: invalidate });
}

export function useRetryPaymentMutation() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: () => billingControllerRetryPaymentV1(),
    onSuccess: invalidate,
  });
}

export function useUpdatePaymentMethodMutation() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (card: CardDto) => billingControllerUpdatePaymentMethodV1({ card }),
    onSuccess: invalidate,
  });
}

export function useContactSalesMutation() {
  return useMutation({
    mutationFn: (dto: CreateSalesRequestDto) => billingControllerContactSalesV1(dto),
  });
}
